import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { determineVotes } from '@core/business-logic';
import axios from '../../http/index.js';
import { sendVirtualPageView } from '../../shared/index.js';
import { useProduct } from '../../shared/productContext.jsx';
import { useJoinedCategories, usePhases } from '../../stores/index.js';

/** Voting-Modell, für das die Projektliste Finalisten filtert und Ränge zeigt. */
const COMPETITION_VOTING_MODEL = 'competition-ranking';

/** Freitextsuche über Teilnehmername und Projekttitel. */
const matchesPhrase = (project, phrase) => {
    const haystack =
        project.contestantName.toLowerCase() + (project.projectTitle ? ' ' + project.projectTitle.toLowerCase() : '');
    return haystack.includes(phrase.toLowerCase());
};

/**
 * Rang eines Projekts in der Wettbewerbs-Liste.
 *
 * Nur der Wettbewerb kennt Ränge; die Förder-Logik (Fördertopf) zeigt keinen.
 * Der Rang ist erst öffentlich, wenn die jeweilige Phase läuft und Stimmen da
 * sind — deshalb dieselbe Staffelung wie in der bisherigen Bigfoot-Liste.
 */
const resolveCompetitionRank = (project, phases) => {
    const { appStatus, voteStarted } = phases;

    if (appStatus === 'FINALE') {
        return project.voteCountFinale ? project.rank : '-';
    }
    if (appStatus === 'QUALIFICATION' && voteStarted) {
        return project.voteCount ? project.rank : '-';
    }
    if (appStatus === 'CLOSED') {
        return project.rank || '-';
    }
    return '-';
};

/**
 * Daten und Logik der Projektliste: Metadaten (`/cta/`, `/options/`, `/ecb/`),
 * Projekte (`/projects/?category=`), der Kategorie-Zustand in der URL, die
 * Freitextsuche und die daraus gebaute Anzeigereihenfolge inklusive der
 * eingestreuten ECB-Karten.
 *
 * Die Darstellung (Tabelle vs. Raster) entscheidet allein `ProjectList.jsx` über
 * `product.projectListView`; hier entstehen nur neutrale Einträge.
 *
 * @returns {{
 *   votingPhases: object,
 *   projectsCount: number,
 *   listItems: Array<object> | null,
 *   phrase: string,
 *   categoryOptions: object | null,
 *   categorySelected: string,
 *   cta: object | null,
 *   inputSelection: boolean,
 *   categoryHandler: (event: object) => void,
 *   onChangeHandler: (event: object) => void,
 * }}
 */
export const useProjectListData = () => {
    const product = useProduct();
    const votingPhases = usePhases();
    const joinedCategories = useJoinedCategories();

    const [projects, setProjects] = useState(null);
    const [phrase, setPhrase] = useState('');
    const [categoryOptions, setCategoryOptions] = useState(null);
    const [categorySelected, setCategorySelected] = useState('');
    const [cta, setCta] = useState(null);
    const [ecb, setEcb] = useState([]);
    const [inputSelection, setInputSelection] = useState(true);

    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        const ctaRequest = axios.get('/cta/', { params: { page: 'projekte' } });
        const optionsRequest = axios.get('/options/');
        const ecbRequest = axios.get('/ecb/');

        Promise.all([ctaRequest, optionsRequest, ecbRequest])
            .then(([resCta, resOptions, resEcb]) => {
                setCta(resCta.data[0]);
                setEcb(resEcb.data);

                const availableCategories = resOptions.data[0]?.projectForm?.projectCategory;
                if (availableCategories) {
                    availableCategories.dependent = joinedCategories;
                }
                if (availableCategories && availableCategories.elementConfig?.options?.length > 1) {
                    const firstOption = () => {
                        if (availableCategories.dependent) {
                            return { value: '', displayValue: 'Alle' };
                        }
                        return {
                            value: '',
                            displayValue: 'Eine ' + availableCategories.elementConfig.label + ' wählen',
                            disabled: true,
                        };
                    };

                    availableCategories.elementConfig.options.unshift(firstOption());
                    availableCategories.elementConfig.label =
                        'Teilnehmende nach ' + availableCategories.elementConfig.label + ' filtern';
                    setCategoryOptions(availableCategories);
                }
            })
            .catch((error) => {
                console.error('Error loading project list metadata:', error);
            });
        sendVirtualPageView('Teilnehmer');
        // `joinedCategories` steht beim Mounten bereits fest (ConfigProvider wartet auf die Config).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const query = qs.parse(location.search.slice(1)).category;
        setProjects(null);
        axios
            .get(`/projects/?category=${query || ''}`)
            .then((response) => {
                setProjects(response.data);
                setCategorySelected(query !== undefined ? query : '');
                setInputSelection(true);
            })
            .catch((error) => {
                console.error('Error fetching projects:', error);
            });
    }, [location]);

    const categoryHandler = (event) => {
        const selectedCategory = event.target.value;
        setPhrase('');
        setInputSelection(false);
        if (selectedCategory !== categorySelected) {
            setCategorySelected(selectedCategory);
            history.push({
                search: `?category=${selectedCategory}`,
            });
        }
    };

    const onChangeHandler = (event) => {
        setPhrase(event.target.value);
        if (categorySelected !== '' && categoryOptions?.dependent) {
            setCategorySelected('');
            history.push({
                search: `?category=`,
            });
        }
    };

    const projectsCount = projects ? projects.length : 0;
    const isCompetition = product.votingModel === COMPETITION_VOTING_MODEL;

    // Stimmenzahl-Regel des Produkts; ohne Deskriptor-Feld gilt die geteilte Regel
    // (`determineVotes` = Cassiopeia). Beide Verbraucher (Rang und Screenreader-Text)
    // lesen denselben Wert, damit sie nicht auseinanderlaufen.
    const resolveVoteCount =
        product.resolveVoteCount || (({ project, phases }) => determineVotes(project.voteCount, phases));

    let listItems = null;

    if (projects && votingPhases) {
        const visibleProjects = projects
            .filter((project) =>
                // Im Finale zeigt der Wettbewerb nur die Finalisten (bzw. alle, wenn es keine Qualifikation gab).
                isCompetition && votingPhases.appStatus === 'FINALE'
                    ? project.isFinalist || !votingPhases.hasQualification
                    : true
            )
            .filter((project) => matchesPhrase(project, phrase));

        listItems = [];
        visibleProjects.forEach((project, index) => {
            // ECB-Karte an der Position ihrer `order` (0-basiert, bezogen auf die Anzeige-Reihenfolge).
            // Nur explizit versteckte Karten fallen raus: `isVisible !== false` lässt Karten ohne
            // das Feld sichtbar (Legacy- bzw. angemeldete Antworten). Das Bigfoot-Schema setzt
            // `isVisible` per Vorgabe auf `true`, dort bleibt das Verhalten unverändert.
            const ecbCard = ecb.find(
                (item) => item?.isVisible !== false && typeof item.order === 'number' && item.order === index
            );
            if (ecbCard) {
                listItems.push({ type: 'ecb', key: `ecb-${ecbCard._id}`, ecb: ecbCard });
            }

            listItems.push({
                type: 'project',
                key: project._id,
                project,
                rank: isCompetition ? resolveCompetitionRank(project, votingPhases) : '-',
                voteCount: resolveVoteCount({ project, phases: votingPhases }),
                projectBalance: project.fundingAccount ? project.fundingAccount.balance : 0,
            });
        });
    }

    return {
        votingPhases,
        projectsCount,
        listItems,
        phrase,
        categoryOptions,
        categorySelected,
        cta,
        inputSelection,
        categoryHandler,
        onChangeHandler,
    };
};

export default useProjectListData;
